import { useEffect, useRef } from "react";
import { ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const NavArrow = ({ dir, onClick }) => {
  const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dir === "prev" ? "Previous piece" : "Next piece"}
      className={`absolute top-1/2 -translate-y-1/2 ${dir === "prev" ? "left-2" : "right-2"} w-12 h-12 md:w-14 md:h-14 bg-[var(--obvious-yellow)] border-4 border-[var(--obvious-black)] shadow-[4px_4px_0px_0px_#000] flex items-center justify-center hover:bg-[var(--obvious-coral)] hover:text-white active:translate-x-[2px] active:translate-y-[calc(-50%+2px)] active:shadow-none transition-colors`}
      data-testid={`gallery-lightbox-${dir}-btn`}
    >
      <Icon size={28} strokeWidth={3} />
    </button>
  );
};

export const GalleryLightbox = ({ items, index, onChange }) => {
  const item = index === null ? null : items[index];
  const touchX = useRef(null);
  const go = (step) => onChange((index + step + items.length) % items.length);

  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
  };

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onChange(null)}>
      <DialogContent
        className="max-w-5xl w-[95vw] p-4 pt-12 bg-[var(--obvious-white)] border-4 border-[var(--obvious-black)] shadow-[12px_12px_0px_0px_#000] rounded-none"
        data-testid="gallery-lightbox"
      >
        {item && (
          <>
            <span className="absolute top-4 left-4 font-['Space_Mono'] text-xs font-bold" data-testid="gallery-lightbox-counter">
              {index + 1} / {items.length}
            </span>
            <div
              className="relative bg-[var(--obvious-black)]"
              onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
              onTouchEnd={onTouchEnd}
            >
              <img
                key={item.id}
                src={item.image_url}
                alt={item.title}
                className="w-full max-h-[62vh] object-contain animate-in fade-in duration-300"
                data-testid="gallery-lightbox-image"
              />
              {items.length > 1 && (
                <>
                  <NavArrow dir="prev" onClick={() => go(-1)} />
                  <NavArrow dir="next" onClick={() => go(1)} />
                </>
              )}
            </div>
            <div className="pt-2">
              <span className="font-['Space_Mono'] text-xs bg-[var(--obvious-teal)] text-black px-2 py-1 uppercase">
                {item.category}
              </span>
              <DialogTitle className="font-['Anton'] text-2xl md:text-3xl mt-2 font-normal" data-testid="gallery-lightbox-title">
                {item.title}
              </DialogTitle>
              <DialogDescription className="font-['Space_Mono'] text-sm text-gray-700">
                {item.description}
              </DialogDescription>
              {item.etsy_url && (
                <a
                  href={item.etsy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-obvious btn-coral inline-flex items-center gap-2 mt-4"
                  data-testid="gallery-lightbox-buy-btn"
                >
                  BUY ON ETSY <ExternalLink size={20} strokeWidth={3} />
                </a>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
